import type { Button } from '@hologram-dimension/button';
import type { DatePicker } from '@hologram-dimension/date-picker';
import type { StripeAddressElementChangeEvent } from '@stripe/stripe-js';
import type { DateTime, Duration } from 'luxon';
import type { ReactNode } from 'react';
import type { SimsDaoHologramSimIds, SimsDaoSim } from 'types/__generated__/SimInventoryApi';

// Local type declaration for RequireSubset
type RequireSubset<T, K extends keyof T> = Pick<Required<T>, K> & Omit<T, K>;

// TODO: I'm not sure why I didn't go with Enums here for some things, should switch to that

export type SimKey = keyof SimsDaoSim;

export type TextFilterCondition = 'is' | 'startswith' | 'endswith' | 'contains';

export interface TextFilter {
  condition: TextFilterCondition;
  value: string;
}

export type DataUsageCondition = 'Greater than or equal to (≥)' | 'Less than (<)';

export type DataUsageUnit = 'KB' | 'MB' | 'GB';

export type SimInventorySegmentName = 'all' | 'active' | 'preflight' | 'deactivated';

export type OnPageView = (pageName: string) => void;

export type OnButtonClick = (
  buttonName: string,
  url?: string | URL,
  target?: string,
  external?: boolean
) => void;

export interface Address {
  name: string | null;
  line1: string | null;
  line2?: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  zip: string | null;
}

export type StripeAddress = StripeAddressElementChangeEvent['value']['address'];
export interface NewAddress extends StripeAddress {
  name: string;
}

export type UpdateBillingAddressCallback = (newAddress: NewAddress) => Promise<void>;

export const SimDetailsViews = {
  details: 'Status',
  simanddevices: 'SIM and device',
  profiles: 'Profiles',
  recentsessions: 'Recent sessions',
  usage: 'Data usage',
  coverage: 'Coverage',
  pricing: 'Pricing',
  webhooks: 'Webhooks',
  routes: 'Routes',
};

export type SimDetailsView = keyof typeof SimDetailsViews | null;

export const AccountViews = {
  invoices: 'Invoices',
  settings: 'Settings',
};

export type AccountView = keyof typeof AccountViews | null;

export type OnSubmit = { onSubmit: () => Promise<void> } | undefined;

export type TriggerRef =
  | {
      trigger: () => void;
    }
  | undefined;

export type OnSimsModified = (modifiedSims?: Array<HologramSimId>) => void;

export type OpenModalCallback = (props: {
  modalName: string;
  modalProps?: Record<string, any>;
}) => void;

export type SimInventoryBulkStateAction = 'pause' | 'resume';

export interface SuccessfulSwitch {
  hologramSimId: HologramSimId;
  newProfileName: string;
  deviceName: string;
}

export interface DateFilter {
  timeframe?: string;
  start?: string;
  end?: string;
}

export interface TimeFrameOption {
  value: 'custom' | 'thisMonth' | 'nextMonth' | Duration;
  label: string;
  isFuture?: boolean;
}

export interface DateRange {
  startTime: DateTime;
  endTime: DateTime;
}

export type DatePickerProps = Omit<
  GetReactParameterBag<typeof DatePicker>,
  'id' | 'aria-describedby' | 'aria-label'
>;

export type ActivateButtonRenderer = (props: {
  children?: ReactNode;
  variant: GetReactParameterBag<typeof Button>['variant'];
  id?: string;
}) => ReactNode;

export type LegacyDashboardComponentRenderer = (props: any) => ReactNode;

export enum EnumSimInventoryExportType {
  XLSX,
  CSV,
  JSON,
}

export const exportTypeToLabel = (exportType: EnumSimInventoryExportType): string | undefined => {
  switch (exportType) {
    case EnumSimInventoryExportType.XLSX:
      return 'XLSX';
    case EnumSimInventoryExportType.CSV:
      return 'CSV';
    case EnumSimInventoryExportType.JSON:
      return 'JSON';
    default:
      return undefined;
  }
};

export type HologramSimIds = RequireSubset<SimsDaoHologramSimIds, 'hologramSimId'>;
export type HologramSimIdsMap = Map<
  HologramSimIds['hologramSimId'],
  HologramSimIds['claimed_device_id']
>;

export type AlertToggle = 'noalert' | 'alert';
